import React, { useEffect, useMemo, useState } from "react";

const PROVIDERS = [
  { id: "open_meteo", label: "Open-Meteo", note: "키 없음, 일반+해양 1차" },
  { id: "weatherapi", label: "WeatherAPI", note: "키 필요, 일반 예보 보조" },
  { id: "stormglass", label: "Stormglass", note: "키 필요, 해양 전문" },
  { id: "kma", label: "기상청", note: "키 저장, 직접 연동 전 Open-Meteo 대체" },
];

const PRESETS = [
  { name: "부산", lat: 35.1796, lon: 129.0756 },
  { name: "통영", lat: 34.8544, lon: 128.4332 },
  { name: "여수", lat: 34.7604, lon: 127.6622 },
  { name: "목포", lat: 34.8118, lon: 126.3922 },
  { name: "군산", lat: 35.9677, lon: 126.7366 },
  { name: "인천", lat: 37.4563, lon: 126.7052 },
  { name: "포항", lat: 36.0190, lon: 129.3435 },
  { name: "제주", lat: 33.4996, lon: 126.5312 },
];

const MAP_BOUNDS = {
  minLat: 33.0,
  maxLat: 38.6,
  minLon: 125.0,
  maxLon: 131.2,
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function latLonToPoint(lat, lon) {
  const x = ((lon - MAP_BOUNDS.minLon) / (MAP_BOUNDS.maxLon - MAP_BOUNDS.minLon)) * 100;
  const y = ((MAP_BOUNDS.maxLat - lat) / (MAP_BOUNDS.maxLat - MAP_BOUNDS.minLat)) * 100;
  return { x: clamp(x, 0, 100), y: clamp(y, 0, 100) };
}

function pointToLatLon(xPercent, yPercent) {
  const lon = MAP_BOUNDS.minLon + (xPercent / 100) * (MAP_BOUNDS.maxLon - MAP_BOUNDS.minLon);
  const lat = MAP_BOUNDS.maxLat - (yPercent / 100) * (MAP_BOUNDS.maxLat - MAP_BOUNDS.minLat);
  return { lat: Number(lat.toFixed(4)), lon: Number(lon.toFixed(4)) };
}

function WeatherSettingsPanel({ settings, onLoad, onSave, onTest }) {
  const [form, setForm] = useState(settings || {});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState("");
  const [testLocation, setTestLocation] = useState(settings?.default_location || "부산");
  const [forecastType, setForecastType] = useState("marine");

  useEffect(() => {
    if (!settings) onLoad?.();
  }, [settings, onLoad]);

  useEffect(() => {
    if (settings) {
      setForm(settings);
      setTestLocation(settings.default_location || "부산");
    }
  }, [settings]);

  const enabledProviders = useMemo(() => {
    return String(form.enabled_providers || "open_meteo").split(",").map((item) => item.trim()).filter(Boolean);
  }, [form.enabled_providers]);

  const point = latLonToPoint(Number(form.default_latitude || 35.1796), Number(form.default_longitude || 129.0756));
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleProvider = (providerId) => {
    const next = enabledProviders.includes(providerId)
      ? enabledProviders.filter((item) => item !== providerId)
      : [...enabledProviders, providerId];
    update("enabled_providers", (next.length ? next : ["open_meteo"]).join(","));
  };

  const selectLocation = (item) => {
    setTestLocation(item.name);
    setForm((prev) => ({
      ...prev,
      default_location: item.name,
      default_latitude: item.lat,
      default_longitude: item.lon,
    }));
  };

  const handleMapClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    const next = pointToLatLon(x, y);
    setForm((prev) => ({
      ...prev,
      default_location: "지도 선택",
      default_latitude: next.lat,
      default_longitude: next.lon,
    }));
    setTestLocation("지도 선택");
  };

  const save = async () => {
    setBusy(true);
    setMessage("");
    try {
      const data = await onSave(form);
      setForm(data.settings || form);
      setMessage("날씨 API 설정을 저장했습니다.");
    } catch (err) {
      setMessage(err.message || "날씨 API 설정을 저장하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const test = async (forceRefresh = false) => {
    setBusy(true);
    setMessage("");
    try {
      const data = await onTest({
        location: testLocation,
        forecast_type: forecastType,
        force_refresh: forceRefresh,
        compare: true,
      });
      setResult(data.result || data);
    } catch (err) {
      setResult({ ok: false, error: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-card weather-admin-card">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">날씨 엔진</span>
          <h3>날씨 API 관리</h3>
          <p>여러 API를 비교하고 로컬 캐시를 우선 사용해 민감한 해양/출조 판단을 보조합니다.</p>
        </div>
        <button className="btn-secondary" onClick={onLoad} disabled={busy}>새로고침</button>
      </div>

      {message && <div className={`weather-inline-message ${message.includes("못했습니다") || message.includes("오류") || message.includes("실패") ? "error" : ""}`}>{message}</div>}

      <div className="weather-layout">
        <div className="weather-settings-column">
          <div className="weather-section">
            <h4>공급자</h4>
            <div className="provider-grid">
              {PROVIDERS.map((provider) => (
                <button
                  key={provider.id}
                  type="button"
                  className={enabledProviders.includes(provider.id) ? "provider-toggle active" : "provider-toggle"}
                  onClick={() => toggleProvider(provider.id)}
                >
                  <strong>{provider.label}</strong>
                  <span>{provider.note}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="weather-form-grid">
            <label>
              <span>기본 공급자</span>
              <select value={form.active_provider || "open_meteo"} onChange={(event) => update("active_provider", event.target.value)}>
                {PROVIDERS.map((provider) => <option key={provider.id} value={provider.id}>{provider.label}</option>)}
              </select>
            </label>
            <label>
              <span>공급자 우선순위</span>
              <input value={form.provider_priority || "open_meteo,weatherapi,stormglass,kma"} onChange={(event) => update("provider_priority", event.target.value)} />
            </label>
            <label>
              <span>로컬 캐시</span>
              <select value={String(form.use_local_cache ?? true)} onChange={(event) => update("use_local_cache", event.target.value === "true")}>
                <option value="true">사용</option>
                <option value="false">사용 안 함</option>
              </select>
            </label>
            <label>
              <span>캐시 유지 시간</span>
              <input type="number" min="1" max="1440" value={form.cache_minutes ?? 30} onChange={(event) => update("cache_minutes", event.target.value)} />
            </label>
            <label>
              <span>파고 주의 기준(m)</span>
              <input type="number" step="0.1" value={form.wave_height_warn_m ?? 1.5} onChange={(event) => update("wave_height_warn_m", event.target.value)} />
            </label>
            <label>
              <span>풍속 주의 기준</span>
              <input type="number" step="0.1" value={form.wind_speed_warn_ms ?? 10} onChange={(event) => update("wind_speed_warn_ms", event.target.value)} />
            </label>
          </div>

          <div className="weather-section">
            <h4>API 키</h4>
            <div className="weather-form-grid single">
              <label>
                <span>기상청 Service Key</span>
                <input value={form.kma_service_key || ""} onChange={(event) => update("kma_service_key", event.target.value)} placeholder="공공데이터포털 키" />
              </label>
              <label>
                <span>Stormglass API Key</span>
                <input value={form.stormglass_api_key || ""} onChange={(event) => update("stormglass_api_key", event.target.value)} placeholder="해양 날씨 전문 키" />
              </label>
              <label>
                <span>WeatherAPI Key</span>
                <input value={form.weatherapi_key || ""} onChange={(event) => update("weatherapi_key", event.target.value)} placeholder="일반 날씨 보조 키" />
              </label>
            </div>
          </div>
        </div>

        <div className="weather-map-column">
          <div className="weather-map" onClick={handleMapClick}>
            <div className="map-sea" />
            <div className="map-land" />
            <div className="map-grid-lines" />
            {PRESETS.map((item) => {
              const p = latLonToPoint(item.lat, item.lon);
              return <button key={item.name} className="map-port" style={{ left: `${p.x}%`, top: `${p.y}%` }} onClick={(event) => { event.stopPropagation(); selectLocation(item); }}>{item.name}</button>;
            })}
            <div className="map-marker" style={{ left: `${point.x}%`, top: `${point.y}%` }} />
          </div>

          <div className="map-details">
            <label>
              <span>선택 지역</span>
              <input value={form.default_location || ""} onChange={(event) => { update("default_location", event.target.value); setTestLocation(event.target.value); }} />
            </label>
            <label>
              <span>위도</span>
              <input type="number" step="0.0001" value={form.default_latitude ?? ""} onChange={(event) => update("default_latitude", event.target.value)} />
            </label>
            <label>
              <span>경도</span>
              <input type="number" step="0.0001" value={form.default_longitude ?? ""} onChange={(event) => update("default_longitude", event.target.value)} />
            </label>
          </div>

          <div className="panel-actions weather-actions">
            <input value={testLocation} onChange={(event) => setTestLocation(event.target.value)} placeholder="테스트 지역" />
            <select value={forecastType} onChange={(event) => setForecastType(event.target.value)}>
              <option value="general">일반 날씨</option>
              <option value="marine">해양/출조</option>
            </select>
            <button className="btn-primary" onClick={() => test(false)} disabled={busy}>다중 API 비교</button>
            <button className="btn-secondary" onClick={() => test(true)} disabled={busy}>강제 새로고침</button>
            <button className="btn-primary" onClick={save} disabled={busy}>설정 저장</button>
          </div>
        </div>
      </div>

      {result && (
        <div className={result.ok === false ? "admin-result error" : "admin-result"}>
          <strong>비교 결과</strong>
          {result.combined_risk && (
            <div className="weather-risk-summary">
              <span>통합 위험도</span>
              <strong>{result.combined_risk.level} / {result.combined_risk.score}</strong>
              <p>{result.combined_risk.note}</p>
            </div>
          )}
          <div className="weather-provider-results">
            {(result.providers || [result]).map((item, index) => (
              <div key={`${item.provider || index}-${index}`} className={item.ok ? "weather-provider-result" : "weather-provider-result failed"}>
                <strong>{PROVIDERS.find((provider) => provider.id === item.provider)?.label || item.provider || "공급자"}</strong>
                {item.ok ? <pre>{item.summary_text || JSON.stringify(item, null, 2)}</pre> : <p>{item.message || item.error || "결과 없음"}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WeatherSettingsPanel;
